import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import api from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { ensureNativeFirebaseApp, shouldRegisterNativePush } from '@/lib/firebaseBootstrap';
import { playCachedDispatchRingtone, syncDispatchRingtone } from '@/lib/dispatchRingtone';
import {
  incomingOfferPath,
  isNativePlatform,
  parseIncomingOfferData,
} from '@/lib/providerPush';

const LOG = '[resqly:push]';

/**
 * Register FCM device token and handle incoming-offer notification taps.
 * On web, registration is skipped (no FCM); navigation helpers still work in-app.
 */
export default function useProviderPushNotifications(enabled) {
  const navigate = useNavigate();
  const { token: authToken, role, loading: authLoading } = useAuth();
  const postedTokenRef = useRef(null);

  useEffect(() => {
    if (!enabled) return undefined;
    if (authLoading) {
      console.info(LOG, 'waiting for provider session');
      return undefined;
    }
    if (!authToken || role !== 'provider') {
      console.info(LOG, 'skip: provider JWT not ready', { hasToken: Boolean(authToken), role });
      return undefined;
    }

    let removeListeners = () => {};
    let cancelled = false;

    const setup = async () => {
      if (!isNativePlatform()) {
        console.info(LOG, 'skip: not a native platform (web has no FCM)');
        return;
      }

      const { PushNotifications } = await import('@capacitor/push-notifications');

      const openFromData = (data) => {
        const parsed = parseIncomingOfferData(data);
        if (parsed?.offerId) {
          navigate(incomingOfferPath(parsed.offerId));
        }
      };

      const syncDispatchChannel = async () => {
        try {
          // Creates/updates the incoming-request channel via the native plugin
          // using the server-returned channel_id and the downloaded admin
          // ringtone (FileProvider content:// URI) — never a raw resource name.
          await syncDispatchRingtone();
        } catch (err) {
          console.info(LOG, 'dispatch channel sync failed (non-fatal)', err?.message || err);
        }
      };

      const registerToken = async (token) => {
        if (!token) {
          console.info(LOG, 'registration event had empty token');
          return;
        }
        if (postedTokenRef.current === token) {
          console.info(LOG, 'skip duplicate device-token POST for same token');
          return;
        }
        const jwt = localStorage.getItem('resqly_token');
        if (!jwt) {
          console.info(LOG, 'skip device-token POST: JWT missing');
          return;
        }
        const payload = {
          token,
          platform: Capacitor.getPlatform() === 'ios' ? 'ios' : 'android',
        };
        console.info(LOG, 'device-token API request', { platform: payload.platform });
        try {
          const { data } = await api.post('/providers/me/device-tokens', payload);
          postedTokenRef.current = token;
          console.info(LOG, 'device-token API response', {
            success: data?.success,
            platform: data?.device?.platform,
            active: data?.device?.active,
          });
        } catch (err) {
          console.info(LOG, 'device-token API error', err?.response?.status, err?.message || err);
        }
      };

      await syncDispatchChannel();
      if (cancelled) return;

      let perm;
      try {
        perm = await PushNotifications.requestPermissions();
      } catch (err) {
        console.info(LOG, 'permission request failed (non-fatal)', err?.message || err);
        return;
      }
      console.info(LOG, 'permission result', perm);
      if (perm.receive !== 'granted') {
        console.info(LOG, 'skip register: notification permission not granted');
        return;
      }

      const firebaseReady = await ensureNativeFirebaseApp();
      console.info(LOG, 'firebase ready', firebaseReady);
      if (!shouldRegisterNativePush({ isNative: true, firebaseReady })) {
        console.info(LOG, 'skip register: FirebaseApp not initialized');
        return;
      }

      const errorHandle = await PushNotifications.addListener('registrationError', (ev) => {
        postedTokenRef.current = null;
        console.info(LOG, 'registration error', ev?.error || ev);
      });

      const regHandle = await PushNotifications.addListener('registration', (ev) => {
        console.info(LOG, 'registration token received');
        registerToken(ev.value);
      });

      const actionHandle = await PushNotifications.addListener(
        'pushNotificationActionPerformed',
        (ev) => {
          openFromData(ev.notification?.data || {});
        },
      );

      const receivedHandle = await PushNotifications.addListener(
        'pushNotificationReceived',
        (ev) => {
          const data = ev.notification?.data || {};
          const parsed = parseIncomingOfferData(data);
          playCachedDispatchRingtone(parsed?.offerId);
          openFromData(data);
        },
      );

      removeListeners = () => {
        errorHandle.remove();
        regHandle.remove();
        actionHandle.remove();
        receivedHandle.remove();
      };

      try {
        console.info(LOG, 'registration started');
        await PushNotifications.register();
        console.info(LOG, 'PushNotifications.register() resolved');
      } catch (err) {
        console.info(LOG, 'PushNotifications.register() failed (non-fatal)', err?.message || err);
      }
    };

    setup().catch((err) => {
      console.info(LOG, 'setup failed (non-fatal)', err?.message || err);
    });

    return () => {
      cancelled = true;
      removeListeners();
    };
  }, [enabled, authToken, role, authLoading, navigate]);
}
